/**
 * JWT utility functions for decoding and managing tokens
 */

import { devError } from './devLog';

/** Decoded JWT claims (payload). Standard claims are typed; others are allowed. */
export interface JwtPayload {
	exp?: number;
	iat?: number;
	/** The organization the session works in (client sessions only). */
	organizationId?: string;
	[key: string]: unknown;
}

/**
 * Decodes a JWT token without verifying the signature
 * Used to extract claims like expiration time
 */
export function decodeJWT(token: string): JwtPayload | null {
	try {
		// JWT format: header.payload.signature
		const parts = token.split('.');
		if (parts.length !== 3) {
			throw new Error('Invalid JWT format');
		}

		// Decode the payload (second part)
		const payload = parts[1];
		const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
		return JSON.parse(decoded);
	} catch (error) {
		devError('Failed to decode JWT:', error);
		return null;
	}
}

/**
 * Gets the expiration time from a JWT token
 * @returns Expiration time in milliseconds or null if not found
 */
export function getTokenExpiration(token: string): number | null {
	const decoded = decodeJWT(token);
	if (!decoded || !decoded.exp) {
		return null;
	}

	// JWT exp is in seconds, convert to milliseconds
	return decoded.exp * 1000;
}

/**
 * Calculates the time remaining until token expiration
 * @returns Time in milliseconds until expiration, or 0 if expired
 */
export function getTimeUntilExpiration(token: string): number {
	const expiration = getTokenExpiration(token);
	if (!expiration) {
		return 0;
	}

	const now = Date.now();
	const timeUntilExpiration = expiration - now;

	return Math.max(0, timeUntilExpiration);
}

/**
 * Calculates when to refresh the token (with safety margin)
 * @param token The JWT token
 * @param marginMinutes Minutes before expiration to refresh (default: 1)
 * @returns Time in milliseconds until refresh should happen
 */
export function getTimeUntilRefresh(token: string, marginMinutes: number = 1): number {
	const timeUntilExpiration = getTimeUntilExpiration(token);
	if (timeUntilExpiration === 0) {
		return 0; // Already expired
	}

	// Subtract safety margin
	const marginMs = marginMinutes * 60 * 1000;
	const timeUntilRefresh = timeUntilExpiration - marginMs;

	// If we're already within the margin, refresh immediately
	return Math.max(0, timeUntilRefresh);
}
